import { useCallback, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  buildDraftTestRequest,
  canRunDraftTest,
} from "../lib/pmsCredentialsTest";
import { testPmsCredentials } from "../services/PmsCredentialsTestService";
import type { Connector, Credentials } from "../components/shared/ConnectorCredentialsForm";
import type { TestCredentialsResponse } from "../types/connectivity";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

interface RunDraftTestParams {
  connector: Connector | null;
  credentials: Credentials;
  hotelCode: string;
}

export function usePmsCredentialTest() {
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestCredentialsResponse | null>(
    null
  );
  const [testError, setTestError] = useState<string | null>(null);

  const clearTestResult = useCallback(() => {
    setTestResult(null);
    setTestError(null);
  }, []);

  const getSessionToken = useCallback(async (): Promise<string> => {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session?.access_token) {
      throw new Error("Unable to test connection right now. Please try again.");
    }

    return session.access_token;
  }, []);

  const runDraftTest = useCallback(
    async ({ connector, credentials, hotelCode }: RunDraftTestParams) => {
      setTestLoading(true);
      setTestResult(null);
      setTestError(null);

      try {
        if (!hotelCode?.trim()) {
          throw new Error(
            "Hotel code is not available yet. Complete the hotel details step first."
          );
        }

        if (!connector || !canRunDraftTest(connector, credentials)) {
          throw new Error(
            "Please fill in all required connector fields before testing."
          );
        }

        const token = await getSessionToken();
        const body = buildDraftTestRequest(connector, credentials);
        const response = await testPmsCredentials(
          API_BASE_URL,
          token,
          hotelCode.trim(),
          body
        );

        setTestResult(response);
      } catch (err) {
        setTestError(
          err instanceof Error ? err.message : "Failed to test connection"
        );
      } finally {
        setTestLoading(false);
      }
    },
    [getSessionToken]
  );

  const runSavedTest = useCallback(
    async ({ hotelCode }: { hotelCode: string }) => {
      setTestLoading(true);
      setTestResult(null);
      setTestError(null);

      try {
        if (!hotelCode?.trim()) {
          throw new Error("Hotel code is not available for this integration.");
        }

        const token = await getSessionToken();
        const response = await testPmsCredentials(
          API_BASE_URL,
          token,
          hotelCode.trim(),
          { source: "saved", connectorType: "PMS" }
        );

        setTestResult(response);
      } catch (err) {
        setTestError(
          err instanceof Error ? err.message : "Failed to test connection"
        );
      } finally {
        setTestLoading(false);
      }
    },
    [getSessionToken]
  );

  return {
    testLoading,
    testResult,
    testError,
    runDraftTest,
    runSavedTest,
    clearTestResult,
  };
}
